import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, BookOpen, FolderOpen, Calendar, HelpCircle, Users, Heart,
  Briefcase, TrendingUp, Brain, CheckCircle, MessageSquare, MapPin,
  Smile, Frown, Meh, RefreshCw
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const hdr = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const KPI = ({ value, label, color = '#E86C3D', sub }) => (
  <Card className="p-4 rounded-2xl text-center bg-white">
    <p className="text-3xl font-black" style={{ color }}>{value}</p>
    <p className="text-xs text-gray-600 font-semibold mt-1">{label}</p>
    {sub && <p className="text-[10px] text-gray-400 mt-0.5">{sub}</p>}
  </Card>
);

const ProgressBar = ({ label, value, max, color = '#E86C3D' }) => {
  const pct = max > 0 ? Math.round(value / max * 100) : 0;
  return (
    <div className="mb-3">
      <div className="flex justify-between items-center mb-1">
        <span className="text-xs font-semibold text-gray-700">{label}</span>
        <span className="text-xs font-bold" style={{ color }}>{pct}%</span>
      </div>
      <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
};

const AnalyticsDashboard = ({ user }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('workshops');
  const [data, setData] = useState({});
  const [loading, setLoading] = useState({});

  const fetchTab = async (tabName) => {
    if (data[tabName]) return;
    setLoading(p => ({ ...p, [tabName]: true }));
    try {
      const res = await fetch(`${API}/api/analytics/${tabName}`, { headers: hdr() });
      if (res.ok) {
        const json = await res.json();
        setData(p => ({ ...p, [tabName]: json }));
      }
    } catch (e) { console.error(e); }
    setLoading(p => ({ ...p, [tabName]: false }));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchTab(tab); }, [tab]);

  const renderLoading = () => (
    <div className="text-center py-16"><RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-300" /><p className="text-sm text-gray-400 mt-3">Loading analytics...</p></div>
  );

  return (
    <div className="min-h-screen pb-8" style={{ backgroundColor: '#F5F3EF' }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #1B2D4F 0%, #1E6B7B 100%)' }} className="p-6 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <button onClick={() => navigate(-1)} className="p-2 rounded-full bg-white/20 hover:bg-white/30" data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></button>
            <div>
              <h1 className="text-2xl font-bold">Program Analytics</h1>
              <p style={{ color: "rgba(255,255,255,0.7)" }} className="text-sm">Visual dashboards for every aspect of your program</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white rounded-xl p-1 shadow-sm mb-6 overflow-x-auto flex-nowrap w-full justify-start" style={{ scrollbarWidth: 'none' }}>
            {[
              { v: 'workshops', l: 'Courses', i: BookOpen },
              { v: 'goals', l: 'Goals', i: TrendingUp },
              { v: 'impact-projects', l: 'Projects', i: FolderOpen },
              { v: 'assessments', l: 'Assessments', i: CheckCircle },
              { v: 'wins', l: 'Win Wall', i: Briefcase },
              { v: 'events', l: 'Events', i: Calendar },
              { v: 'helpdesk', l: 'Q&A', i: HelpCircle },
              { v: 'parents', l: 'Parents', i: Heart },
              { v: 'staff', l: 'Staff', i: Users },
              { v: 'worksites', l: 'Worksites', i: MapPin },
              { v: 'wellness', l: 'Wellness', i: Smile },
              { v: 'ai-usage', l: 'AI Usage', i: Brain },
            ].map(t => (
              <TabsTrigger key={t.v} value={t.v} className="rounded-lg text-xs gap-1.5 flex-shrink-0 whitespace-nowrap data-[state=active]:bg-[#1B2D4F] data-[state=active]:text-white" data-testid={`tab-${t.v}`}>
                <t.i className="w-3.5 h-3.5" /> {t.l}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* WORKSHOPS */}
          <TabsContent value="workshops">
            {loading.courses ? renderLoading() : data.courses ? (() => {
              const k = data.courses.kpis;
              return (
                <div className="space-y-5">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <KPI value={k.total_completions} label="Completions" color="#8B5CF6" />
                    <KPI value={`${k.total_xp_earned}`} label="Total XP" color="#F59E0B" />
                    <KPI value={k.unique_participants} label="Participants" color="#3B82F6" />
                    <KPI value={`${k.participation_rate}%`} label="Participation" color="#10B981" />
                    <KPI value={k.avg_completions_per_youth} label="Avg/Youth" color="#E86C3D" />
                  </div>
                  <Card className="p-5 rounded-2xl bg-white">
                    <h3 className="font-bold text-sm text-gray-900 mb-4">Course Completion Breakdown</h3>
                    {data.courses.courses?.length > 0 ? data.courses.courses.map((c, i) => (
                      <ProgressBar key={i} label={c.title || c.course_id} value={c.completed} max={c.started || 1} color="#8B5CF6" />
                    )) : <p className="text-gray-400 text-center py-6">No course data yet</p>}
                  </Card>
                </div>
              );
            })() : renderLoading()}
          </TabsContent>

          {/* IMPACT PROJECTS */}
          <TabsContent value="impact-projects">
            {loading['impact-projects'] ? renderLoading() : data['impact-projects'] ? (() => {
              const k = data['impact-projects'].kpis;
              return (
                <div className="space-y-5">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <KPI value={k.total_entries} label="Total Entries" color="#EC4899" />
                    <KPI value={k.unique_participants} label="Participants" color="#8B5CF6" />
                    <KPI value={`${k.participation_rate}%`} label="Participation" color="#10B981" />
                    <KPI value={k.total_sections_completed} label="Sections Done" color="#F59E0B" />
                  </div>
                  <Card className="p-5 rounded-2xl bg-white">
                    <h3 className="font-bold text-sm text-gray-900 mb-4">Project Popularity</h3>
                    {data['impact-projects'].projects?.map((p, i) => (
                      <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-gray-50 mb-2">
                        <span className="font-semibold text-sm">{p.title || p.project_id}</span>
                        <div className="flex gap-3 text-xs">
                          <Badge className="bg-pink-100 text-pink-700">{p.started} started</Badge>
                          <Badge className="bg-violet-100 text-violet-700">{p.sections_completed} sections done</Badge>
                        </div>
                      </div>
                    ))}
                    {!data['impact-projects'].projects?.length && <p className="text-gray-400 text-center py-6">No project data yet</p>}
                  </Card>
                </div>
              );
            })() : renderLoading()}
          </TabsContent>

          {/* EVENTS */}
          <TabsContent value="events">
            {loading.events ? renderLoading() : data.events ? (() => {
              const k = data.events.kpis;
              return (
                <div className="space-y-5">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <KPI value={k.total_events} label="Total Events" color="#3B82F6" />
                    <KPI value={k.total_checkins} label="Check-ins" color="#10B981" />
                    <KPI value={k.completed} label="Completed" color="#8B5CF6" />
                    <KPI value={k.upcoming} label="Upcoming" color="#F59E0B" />
                  </div>
                  <Card className="p-5 rounded-2xl bg-white">
                    <h3 className="font-bold text-sm text-gray-900 mb-4">Event Attendance</h3>
                    {data.events.events?.map((e, i) => (
                      <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-gray-50 mb-2">
                        <div>
                          <p className="font-semibold text-sm">{e.title}</p>
                          <p className="text-xs text-gray-500">{e.date?.split('T')[0]} · {e.type}</p>
                        </div>
                        <div className="flex gap-2">
                          <Badge className="bg-green-100 text-green-700">{e.checkins} attended</Badge>
                          <Badge className="bg-blue-100 text-blue-700">{e.surveys} surveys</Badge>
                        </div>
                      </div>
                    ))}
                    {!data.events.events?.length && <p className="text-gray-400 text-center py-6">No events yet</p>}
                  </Card>
                </div>
              );
            })() : renderLoading()}
          </TabsContent>

          {/* Q&A HELPDESK */}
          <TabsContent value="helpdesk">
            {loading.helpdesk ? renderLoading() : data.helpdesk ? (() => {
              const k = data.helpdesk.kpis;
              return (
                <div className="space-y-5">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <KPI value={k.total_questions} label="Total Questions" color="#E86C3D" />
                    <KPI value={k.pending} label="Pending" color="#EF4444" />
                    <KPI value={k.answered} label="Answered" color="#10B981" />
                    <KPI value={`${k.response_rate}%`} label="Response Rate" color="#3B82F6" />
                    <KPI value={k.avg_per_day} label="Avg/Day" color="#8B5CF6" />
                  </div>
                  <div className="grid md:grid-cols-2 gap-5">
                    <Card className="p-5 rounded-2xl bg-white">
                      <h3 className="font-bold text-sm text-gray-900 mb-3">By Category</h3>
                      {data.helpdesk.categories?.map((c, i) => (
                        <div key={i} className="flex justify-between items-center p-2 rounded-lg bg-gray-50 mb-1.5">
                          <span className="text-sm capitalize">{c.category}</span>
                          <Badge className="bg-orange-100 text-orange-700">{c.count}</Badge>
                        </div>
                      ))}
                    </Card>
                    <Card className="p-5 rounded-2xl bg-white">
                      <h3 className="font-bold text-sm text-gray-900 mb-3">Staff Responses</h3>
                      {data.helpdesk.monitor_responses?.map((m, i) => (
                        <div key={i} className="flex justify-between items-center p-2 rounded-lg bg-gray-50 mb-1.5">
                          <span className="text-sm">{m.name}</span>
                          <Badge className="bg-green-100 text-green-700">{m.answered} answered</Badge>
                        </div>
                      ))}
                    </Card>
                  </div>
                </div>
              );
            })() : renderLoading()}
          </TabsContent>

          {/* PARENTS */}
          <TabsContent value="parents">
            {loading.parents ? renderLoading() : data.parents ? (() => {
              const k = data.parents.kpis;
              return (
                <div className="space-y-5">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <KPI value={k.total_parents} label="Total Parents" color="#EC4899" />
                    <KPI value={k.active_7d} label="Active (7d)" color="#10B981" />
                    <KPI value={`${k.consent_rate}%`} label="Consent Rate" color="#3B82F6" />
                    <KPI value={k.total_messages} label="Messages" color="#F59E0B" />
                  </div>
                  <div className="grid md:grid-cols-2 gap-5">
                    <Card className="p-5 rounded-2xl bg-white">
                      <h3 className="font-bold text-sm text-gray-900 mb-3">Consent Status</h3>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-4 rounded-xl bg-green-50 text-center">
                          <p className="text-2xl font-bold text-green-700">{k.consent_signed}</p>
                          <p className="text-xs text-green-600">Signed</p>
                        </div>
                        <div className="p-4 rounded-xl bg-amber-50 text-center">
                          <p className="text-2xl font-bold text-amber-700">{k.consent_pending}</p>
                          <p className="text-xs text-amber-600">Pending</p>
                        </div>
                      </div>
                    </Card>
                    <Card className="p-5 rounded-2xl bg-white">
                      <h3 className="font-bold text-sm text-gray-900 mb-3">Parent Activity</h3>
                      <ProgressBar label="Active Rate (7 days)" value={k.active_7d} max={k.total_parents} color="#10B981" />
                      <ProgressBar label="Consent Completion" value={k.consent_signed} max={k.consent_signed + k.consent_pending} color="#3B82F6" />
                    </Card>
                  </div>
                </div>
              );
            })() : renderLoading()}
          </TabsContent>

          {/* STAFF */}
          <TabsContent value="staff">
            {loading.staff ? renderLoading() : data.staff ? (() => {
              const k = data.staff.kpis;
              return (
                <div className="space-y-5">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <KPI value={k.total_staff} label="Total Staff" color="#1B2D4F" />
                    <KPI value={k.active_7d} label="Active (7d)" color="#10B981" />
                    <KPI value={k.total_assigned_youth} label="Youth Assigned" color="#3B82F6" />
                    <KPI value={k.total_qa_answered} label="Q&A Answered" color="#8B5CF6" />
                    <KPI value={k.total_qa_pending} label="Q&A Pending" color="#EF4444" />
                  </div>
                  <Card className="p-5 rounded-2xl bg-white">
                    <h3 className="font-bold text-sm text-gray-900 mb-4">Staff Performance</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="bg-gray-50 text-xs text-gray-600">
                          <th className="p-3 text-left">Name</th>
                          <th className="p-3 text-center">Youth</th>
                          <th className="p-3 text-center">Q&A Answered</th>
                          <th className="p-3 text-center">Pending</th>
                          <th className="p-3 text-center">Check-in Reviews</th>
                          <th className="p-3 text-center">Last Login</th>
                          <th className="p-3 text-center">Status</th>
                        </tr></thead>
                        <tbody>
                          {data.staff.staff?.map((s, i) => (
                            <tr key={i} className="border-t border-gray-100">
                              <td className="p-3 font-semibold">{s.name}</td>
                              <td className="p-3 text-center">{s.assigned_youth}</td>
                              <td className="p-3 text-center">{s.qa_answered}</td>
                              <td className="p-3 text-center">{s.qa_pending > 0 ? <span className="text-red-600 font-bold">{s.qa_pending}</span> : '0'}</td>
                              <td className="p-3 text-center">{s.checkin_responses}</td>
                              <td className="p-3 text-center text-xs text-gray-500">{s.last_login ? new Date(s.last_login).toLocaleDateString() : 'Never'}</td>
                              <td className="p-3 text-center"><Badge className={s.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>{s.active ? 'Active' : 'Inactive'}</Badge></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                </div>
              );
            })() : renderLoading()}
          </TabsContent>

          {/* WORKSITES */}
          <TabsContent value="worksites">
            {loading.worksites ? renderLoading() : data.worksites ? (() => {
              const k = data.worksites.kpis;
              return (
                <div className="space-y-5">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <KPI value={k.total_worksites} label="Worksites" color="#1B2D4F" />
                    <KPI value={k.total_youth_placed} label="Youth Placed" color="#10B981" />
                    <KPI value={k.top_worksite?.substring(0, 20)} label="Top Worksite" color="#E86C3D" />
                    <KPI value={k.avg_points_across} label="Avg Points" color="#8B5CF6" />
                  </div>
                  <Card className="p-5 rounded-2xl bg-white">
                    <h3 className="font-bold text-sm text-gray-900 mb-4">Worksite Comparison</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="bg-gray-50 text-xs text-gray-600">
                          <th className="p-3 text-left">Worksite</th>
                          <th className="p-3 text-center">Youth</th>
                          <th className="p-3 text-center">Avg Points</th>
                          <th className="p-3 text-center">Goals</th>
                          <th className="p-3 text-center">Goal Rate</th>
                          <th className="p-3 text-center">Check-ins</th>
                        </tr></thead>
                        <tbody>
                          {data.worksites.worksites?.map((w, i) => (
                            <tr key={i} className="border-t border-gray-100">
                              <td className="p-3 font-semibold">{w.worksite}</td>
                              <td className="p-3 text-center">{w.youth_count}</td>
                              <td className="p-3 text-center font-bold" style={{ color: '#E86C3D' }}>{w.avg_points}</td>
                              <td className="p-3 text-center">{w.completed_goals}/{w.goals}</td>
                              <td className="p-3 text-center"><Badge className={w.goal_rate >= 50 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>{w.goal_rate}%</Badge></td>
                              <td className="p-3 text-center">{w.checkins}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                </div>
              );
            })() : renderLoading()}
          </TabsContent>

          {/* WELLNESS / MOOD */}
          <TabsContent value="wellness">
            {loading.wellness ? renderLoading() : data.wellness ? (() => {
              const k = data.wellness.kpis;
              const moods = data.wellness.mood_distribution || {};
              const total = Object.values(moods).reduce((a, b) => a + b, 0) || 1;
              const moodEmojis = { amazing: '🔥', good: '😊', excited: '🌟', proud: '💪', okay: '😐', rough: '😤', terrible: '😩', unknown: '❓' };
              return (
                <div className="space-y-5">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <KPI value={k.total_checkins} label="Total Check-ins" color="#3B82F6" />
                    <KPI value={`${k.positive_mood_rate}%`} label="Positive Mood" color="#10B981" />
                    <KPI value={moodEmojis[k.most_common_mood] || k.most_common_mood} label="Top Mood" color="#F59E0B" />
                    <KPI value={k.most_common_challenge || 'N/A'} label="Top Challenge" color="#EF4444" />
                  </div>
                  <div className="grid md:grid-cols-2 gap-5">
                    <Card className="p-5 rounded-2xl bg-white">
                      <h3 className="font-bold text-sm text-gray-900 mb-4">Mood Distribution</h3>
                      {Object.entries(moods).sort((a, b) => b[1] - a[1]).map(([mood, count], i) => (
                        <div key={i} className="mb-3">
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-sm">{moodEmojis[mood] || ''} {mood}</span>
                            <span className="text-xs font-bold text-gray-600">{Math.round(count / total * 100)}% ({count})</span>
                          </div>
                          <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full bg-gradient-to-r from-green-400 to-emerald-500" style={{ width: `${count / total * 100}%` }} />
                          </div>
                        </div>
                      ))}
                      {Object.keys(moods).length === 0 && <p className="text-gray-400 text-center py-6">No mood data yet</p>}
                    </Card>
                    <Card className="p-5 rounded-2xl bg-white">
                      <h3 className="font-bold text-sm text-gray-900 mb-4">Top Challenges</h3>
                      {data.wellness.challenges?.map((c, i) => (
                        <div key={i} className="flex justify-between items-center p-2.5 rounded-lg bg-gray-50 mb-1.5">
                          <span className="text-sm">{c.challenge}</span>
                          <Badge className="bg-red-100 text-red-700">{c.count}</Badge>
                        </div>
                      ))}
                      {!data.wellness.challenges?.length && <p className="text-gray-400 text-center py-6">No challenge data yet</p>}
                    </Card>
                  </div>
                </div>
              );
            })() : renderLoading()}
          </TabsContent>

          {/* AI USAGE */}
          <TabsContent value="ai-usage">
            {loading['ai-usage'] ? renderLoading() : data['ai-usage'] ? (() => {
              const k = data['ai-usage'].kpis || {};
              return (
                <div className="space-y-5">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <KPI value={k.total_conversations || 0} label="AI Conversations" color="#8B5CF6" />
                    <KPI value={k.unique_users || 0} label="Youth Using AI" color="#3B82F6" />
                    <KPI value={`${k.usage_rate || 0}%`} label="AI Usage Rate" color="#10B981" />
                    <KPI value={k.knowledge_base_docs || 0} label="KB Documents" color="#F59E0B" />
                    <KPI value={k.avg_per_user || 0} label="Avg/User" color="#E86C3D" />
                  </div>
                  <div className="grid md:grid-cols-2 gap-5">
                    <Card className="p-5 rounded-2xl bg-white">
                      <h3 className="font-bold text-sm text-gray-900 mb-3">Conversation Types</h3>
                      {Object.keys(data['ai-usage'].by_type || {}).length > 0 ? Object.entries(data['ai-usage'].by_type).map(([type, count], i) => (
                        <div key={i} className="flex justify-between items-center p-2 rounded-lg bg-gray-50 mb-1.5">
                          <span className="text-sm capitalize">{type.replace(/_/g, ' ')}</span>
                          <Badge className="bg-violet-100 text-violet-700">{count}</Badge>
                        </div>
                      )) : <p className="text-gray-400 text-center py-4">No AI conversations yet</p>}
                    </Card>
                    <Card className="p-5 rounded-2xl bg-white">
                      <h3 className="font-bold text-sm text-gray-900 mb-3">Top AI Users</h3>
                      {data['ai-usage'].top_users?.length > 0 ? data['ai-usage'].top_users.map((u, i) => (
                        <div key={i} className="flex justify-between items-center p-2 rounded-lg bg-gray-50 mb-1.5">
                          <span className="text-sm font-semibold">{u.name}</span>
                          <Badge className="bg-blue-100 text-blue-700">{u.conversations} chats</Badge>
                        </div>
                      )) : <p className="text-gray-400 text-center py-4">No users yet</p>}
                    </Card>
                  </div>
                </div>
              );
            })() : renderLoading()}
          </TabsContent>

          {/* GOALS */}
          <TabsContent value="goals">
            {loading.goals ? renderLoading() : data.goals ? (() => {
              const g = data.goals;
              return (
                <div className="space-y-5">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <KPI value={g.total_goals} label="Total Goals" color="#10B981" />
                    <KPI value={g.completed} label="Completed" color="#8B5CF6" />
                    <KPI value={g.active} label="Active" color="#3B82F6" />
                    <KPI value={`${g.completion_rate}%`} label="Completion Rate" color="#F59E0B" />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <Card className="p-5 rounded-2xl bg-white">
                      <h3 className="font-bold text-sm text-gray-900 mb-3">Goal Type Breakdown</h3>
                      <ProgressBar label="Short-term Goals" value={g.short_term || 0} max={g.total_goals || 1} color="#10B981" />
                      <ProgressBar label="Long-term Goals" value={g.long_term || 0} max={g.total_goals || 1} color="#8B5CF6" />
                    </Card>
                    <Card className="p-5 rounded-2xl bg-white">
                      <h3 className="font-bold text-sm text-gray-900 mb-3">Youth with Goals</h3>
                      <KPI value={`${g.youth_with_goals}/${g.total_youth}`} label="Youth who set goals" color="#3B82F6" sub={`${g.youth_without_goals} haven't set any`} />
                    </Card>
                  </div>
                </div>
              );
            })() : renderLoading()}
          </TabsContent>

          {/* ASSESSMENTS */}
          <TabsContent value="assessments">
            {loading.assessments ? renderLoading() : data.assessments ? (() => {
              const a = data.assessments;
              return (
                <div className="space-y-5">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <KPI value={a.total_taken} label="Assessments Taken" color="#8B5CF6" />
                    <KPI value={a.pre_count} label="Pre-Assessments" color="#3B82F6" />
                    <KPI value={a.post_count} label="Post-Assessments" color="#10B981" />
                    <KPI value={`${a.completion_rate}%`} label="Completion Rate" color="#F59E0B" />
                  </div>
                  
                  <Card className="p-5 rounded-2xl bg-white">
                    <h3 className="font-bold text-sm text-gray-900 mb-3">Assessment Completion</h3>
                    <ProgressBar label="Pre-Assessment Done" value={a.pre_count} max={a.total_youth || 1} color="#3B82F6" />
                    <ProgressBar label="Post-Assessment Done" value={a.post_count} max={a.total_youth || 1} color="#10B981" />
                    <p className="text-xs text-gray-500 mt-2">{a.total_youth - a.pre_count} youth haven't started pre-assessment</p>
                  </Card>

                  {/* Assessment Types Breakdown */}
                  {a.by_type && Object.keys(a.by_type).length > 0 && (
                    <Card className="p-5 rounded-2xl bg-white">
                      <h3 className="font-bold text-sm text-gray-900 mb-3">By Assessment Type</h3>
                      <div className="space-y-2">
                        {Object.entries(a.by_type).map(([name, info]) => (
                          <div key={name} className="flex items-center justify-between p-3 rounded-xl bg-gray-50">
                            <div>
                              <p className="font-medium text-sm">{name}</p>
                              <p className="text-xs text-gray-500">{info.count} completed</p>
                            </div>
                            <Badge className="bg-violet-100 text-violet-700">Avg: {info.avg_score}%</Badge>
                          </div>
                        ))}
                      </div>
                    </Card>
                  )}

                  {/* Custom Assessments */}
                  {a.custom_assessments?.length > 0 && (
                    <Card className="p-5 rounded-2xl bg-white">
                      <h3 className="font-bold text-sm text-gray-900 mb-3">Custom Assessments Created</h3>
                      {a.custom_assessments.map(ca => (
                        <div key={ca.id} className="flex justify-between items-center p-2 rounded-lg bg-violet-50 mb-1.5">
                          <span className="text-sm font-semibold">{ca.title}</span>
                          <span className="text-xs text-gray-500">{ca.questions} questions</span>
                        </div>
                      ))}
                    </Card>
                  )}

                  {/* Individual Results */}
                  {a.youth_results?.length > 0 && (
                    <Card className="p-5 rounded-2xl bg-white">
                      <h3 className="font-bold text-sm text-gray-900 mb-3">Individual Results ({a.youth_results.length})</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead><tr className="border-b text-left">
                            <th className="p-2 font-bold">Name</th>
                            <th className="p-2 font-bold">Group</th>
                            <th className="p-2 font-bold">Assessment</th>
                            <th className="p-2 font-bold text-right">Score</th>
                          </tr></thead>
                          <tbody>
                            {a.youth_results.map((r, i) => (
                              <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                                <td className="p-2">{r.name}</td>
                                <td className="p-2"><Badge className="text-[10px] bg-gray-100">{r.program_type}</Badge></td>
                                <td className="p-2 text-gray-600">{r.assessment_type}</td>
                                <td className="p-2 text-right font-bold">{r.score != null ? `${r.score}%` : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Card>
                  )}
                </div>
              );
            })() : renderLoading()}
          </TabsContent>

          {/* WIN WALL */}
          <TabsContent value="wins">
            {loading.wins ? renderLoading() : data.wins ? (() => {
              const w = data.wins;
              return (
                <div className="space-y-5">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <KPI value={w.total_posts} label="Total Wins" color="#F59E0B" />
                    <KPI value={w.this_week} label="This Week" color="#10B981" />
                    <KPI value={w.total_celebrations} label="Celebrations" color="#EC4899" />
                    <KPI value={w.total_comments} label="Comments" color="#3B82F6" />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <Card className="p-5 rounded-2xl bg-white">
                      <h3 className="font-bold text-sm text-gray-900 mb-3">Top Win Posters</h3>
                      {w.top_posters?.length > 0 ? w.top_posters.map((u, i) => (
                        <div key={i} className="flex justify-between items-center p-2 rounded-lg bg-gray-50 mb-1.5">
                          <span className="text-sm font-semibold">{u.name}</span>
                          <Badge className="bg-yellow-100 text-yellow-700">{u.count} wins</Badge>
                        </div>
                      )) : <p className="text-gray-400 text-center py-4">No wins yet</p>}
                    </Card>
                    <Card className="p-5 rounded-2xl bg-white">
                      <h3 className="font-bold text-sm text-gray-900 mb-3">Win Categories</h3>
                      {w.categories?.length > 0 ? w.categories.map((c, i) => (
                        <ProgressBar key={i} label={c.category || 'General'} value={c.count} max={w.total_posts || 1} color={['#F59E0B', '#10B981', '#3B82F6', '#8B5CF6'][i % 4]} />
                      )) : <p className="text-gray-400 text-center py-4">No categories</p>}
                    </Card>
                  </div>
                  <button
                    onClick={() => window.open(`${API}/api/reports/wins`, '_blank')}
                    className="w-full py-3 rounded-xl bg-amber-50 text-amber-700 text-sm font-semibold hover:bg-amber-100 transition-colors"
                    data-testid="download-wins-csv"
                  >
                    Download Win Wall Report (CSV)
                  </button>
                </div>
              );
            })() : renderLoading()}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
